import { useEffect } from 'react';
import { ProductShell, Button, Logo } from '@cloudgenie/ui';
import { useAuth, hasCap } from '../AuthContext';

/**
 * id.cloudgenie.co IS the admin console.
 *
 * • Unauthenticated visitors → rich sign-in landing (logo, hero, features).
 * • Authenticated admins     → auto-redirect to /admin.
 * • Authenticated non-admins → helpful "wrong site" card that points
 *   them to finops.cloudgenie.co / studio.cloudgenie.co.
 *
 * There is deliberately no FinOps / Studio product picker here — each
 * product lives on its own subdomain and users go there directly.
 */

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const FINOPS_URL = import.meta.env.VITE_FINOPS_URL || 'https://finops.cloudgenie.co';
const STUDIO_URL = import.meta.env.VITE_STUDIO_URL || 'https://studio.cloudgenie.co';

export default function HomePage() {
  const { user, loading, logout } = useAuth();

  // Auto-redirect admins into the console — id.cloudgenie.co IS the admin console.
  useEffect(() => {
    if (!loading && user && hasCap(user, 'admin.ops')) {
      window.location.replace('/admin');
    }
  }, [loading, user]);

  if (loading) {
    return (
      <ProductShell product="id" title="Loading…" subtitle="Checking your session.">
        <p className="text-center text-sm text-muted-foreground" data-testid="home-loading">One moment…</p>
      </ProductShell>
    );
  }

  // Signed-out: hero landing with sign-in CTAs.
  if (!user) {
    return (
      <ProductShell product="id" title="" subtitle="">
        <div className="max-w-5xl mx-auto py-16 md:py-20" data-testid="home-signed-out">
          {/* Hero */}
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-violet-400/30 bg-violet-400/5 text-violet-400 px-3 py-1 text-[11px] font-medium tracking-[0.15em] uppercase">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 ring-4 ring-violet-400/40" />
              CloudGenie · Identity
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              One admin console for your <span className="bg-gradient-to-br from-violet-400 to-fuchsia-500 bg-clip-text text-transparent">entire multi-cloud</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Users, groups, secret brokers, KMS, SSO, billing, workflows —
              every control plane you need to run CloudGenie at enterprise
              scale, in one place.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <a href="/login"><Button variant="primary" data-testid="home-login-cta">Sign in</Button></a>
              <a href="/signup"><Button variant="outline" data-testid="home-signup-cta">Create account</Button></a>
            </div>
          </div>

          {/* Product hand-off cards */}
          <div className="mt-20 grid gap-4 md:grid-cols-3">
            <FeatureCard
              accent="violet"
              title="Identity & RBAC"
              body="6 built-in roles, capability-based permissions, custom overrides, SAML SSO."
              badge="This site"
            />
            <FeatureCard
              accent="cyan"
              title="FinOps"
              body="Real-time multi-cloud cost, budgets, anomalies, savings recommendations."
              href={FINOPS_URL}
              hrefLabel="Open finops.cloudgenie.co →"
            />
            <FeatureCard
              accent="pink"
              title="Studio"
              body="Schema-typed Terraform, OpenTofu, ARM, CloudFormation, Ansible, Pulumi."
              href={STUDIO_URL}
              hrefLabel="Open studio.cloudgenie.co →"
            />
          </div>

          <div className="mt-16 text-center text-xs text-muted-foreground">
            Part of the CloudGenie multi-cloud FinOps platform ·{' '}
            <a href="https://www.cloudgenie.co" className="hover:text-foreground underline underline-offset-4">Learn more</a>
          </div>
        </div>
      </ProductShell>
    );
  }

  // Authenticated but NOT an admin — id is the wrong site for them.
  return (
    <ProductShell
      product="id"
      title=""
      subtitle=""
    >
      <div className="max-w-lg mx-auto text-center space-y-6 py-16" data-testid="home-non-admin">
        <Logo product="id" size={44} className="mx-auto" />
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">This site is for admins</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Your account is signed in, but this subdomain hosts the CloudGenie admin console. Open one of the products below to get to work.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <a href={FINOPS_URL} data-testid="tile-finops"
            className="block rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm p-5 hover:border-cyan-400/60 hover:bg-cyan-400/5 transition text-left">
            <div className="text-xs uppercase tracking-[0.15em] text-cyan-400 mb-1.5">FinOps</div>
            <div className="text-xs text-muted-foreground">Real-time multi-cloud cost, budgets, anomalies.</div>
            <div className="mt-3 text-xs text-cyan-400">Open finops.cloudgenie.co →</div>
          </a>
          <a href={STUDIO_URL} data-testid="tile-studio"
            className="block rounded-xl border border-border/60 bg-card/40 backdrop-blur-sm p-5 hover:border-pink-400/60 hover:bg-pink-400/5 transition text-left">
            <div className="text-xs uppercase tracking-[0.15em] text-pink-400 mb-1.5">Studio</div>
            <div className="text-xs text-muted-foreground">Schema-typed Terraform / OpenTofu editor.</div>
            <div className="mt-3 text-xs text-pink-400">Open studio.cloudgenie.co →</div>
          </a>
        </div>
        <button onClick={logout} data-testid="home-signout-cta" className="text-xs text-muted-foreground hover:text-foreground transition">
          Sign out
        </button>
      </div>
    </ProductShell>
  );
}

interface FeatureCardProps {
  accent: 'violet' | 'cyan' | 'pink';
  title: string;
  body: string;
  href?: string;
  hrefLabel?: string;
  badge?: string;
}

function FeatureCard({ accent, title, body, href, hrefLabel, badge }: FeatureCardProps) {
  const accentMap = {
    violet: { text: 'text-violet-400', border: 'hover:border-violet-400/60', bg: 'hover:bg-violet-400/5' },
    cyan:   { text: 'text-cyan-400',   border: 'hover:border-cyan-400/60',   bg: 'hover:bg-cyan-400/5' },
    pink:   { text: 'text-pink-400',   border: 'hover:border-pink-400/60',   bg: 'hover:bg-pink-400/5' },
  }[accent];
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-[0.15em] ${accentMap.text}`}>{title}</div>
        {badge && <div className="text-[10px] uppercase tracking-widest text-muted-foreground border border-border/60 rounded px-1.5 py-0.5">{badge}</div>}
      </div>
      <div className="mt-3 text-sm text-muted-foreground leading-relaxed">{body}</div>
      {href && hrefLabel && <div className={`mt-4 text-xs ${accentMap.text}`}>{hrefLabel}</div>}
    </>
  );
  if (href) {
    return (
      <a href={href} className={`block rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-6 ${accentMap.border} ${accentMap.bg} transition text-left`}>
        {inner}
      </a>
    );
  }
  return (
    <div className={`rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-6 text-left`}>
      {inner}
    </div>
  );
}
